/**
 * @fileoverview Translate backend task-family records into one Inbox presentation vocabulary. Canonical task/domain identities and available-action context remain server-owned; adapters normalize display fields without authorizing actions or deriving new business decisions. `TaskInbox.js` owns selection and focused domain panels own their explicit workflows.
 */

export const TASK_FAMILIES = [
  { key: 'all', label: 'All tasks' },
  { key: 'payment_instructions', label: 'Payment instructions' },
  {
    key: 'intake_document_coordination',
    label: 'Intake / Document Coordination',
  },
];

export const TASK_TYPES = [
  { key: 'review_payment_identity', family: 'payment_instructions', label: 'Review payment instructions', icon: 'pi pi-shield' },
  { key: 'verify_payment_banking', family: 'payment_instructions', label: 'Verify banking details', icon: 'pi pi-phone' },
  { key: 'prepare_payment_authorization', family: 'payment_instructions', label: 'Prepare payment authorization', icon: 'pi pi-wallet' },
  { key: 'review_intake', label: 'Resolve intake review', actionLabel: 'Resolve review', icon: 'pi pi-check-square' },
  { key: 'obtain_contact_information', label: 'Get prospect contact information', actionLabel: 'Update contact information', icon: 'pi pi-user-edit' },
  { key: 'review_duplicate', label: 'Duplicate review', actionLabel: 'Review matches', icon: 'pi pi-clone' },
  { key: 'request_documents', label: 'Request documents', actionLabel: 'Open outreach', icon: 'pi pi-send' },
  { key: 'follow_up_documents', label: 'Follow up on documents', actionLabel: 'Open outreach', icon: 'pi pi-clock' },
  { key: 'send_to_case_review', label: 'Send to Case Review', actionLabel: 'Open handoff', icon: 'pi pi-user-plus' },
  { key: 'prepare_engagement_materials', label: 'Prepare engagement materials', actionLabel: 'Open engagement workflow', icon: 'pi pi-file-edit' },
  { key: 'resolve_document_exception', label: 'Resolve document exception', actionLabel: 'Open lead', icon: 'pi pi-exclamation-triangle' },
];

export const TASK_TYPE_BY_KEY = TASK_TYPES.reduce((index, type) => ({
  ...index,
  [type.key]: type,
}), {});

const ACTIVITY_TITLES = {
  generated: 'Task created',
  identity_accept: 'Identity accepted',
  identity_hold: 'Held for follow-up',
  identity_reject: 'Identity rejected',
  source_refreshed: 'Task context refreshed',
  source_resolved: 'Operational need resolved',
  assignment_taken: 'Task taken',
  takeover: 'Task taken over',
  assigned: 'Task assigned',
  reassigned: 'Task reassigned',
  unassigned: 'Task unassigned',
  completed: 'Task completed',
  reopened: 'Task reopened',
};



export function taskActivityTitle(actionType) {
  return ACTIVITY_TITLES[actionType] || 'Task updated';
}



function humanizeTaskTypeKey(value) {
  const label = String(value || '');
  if (!label.includes('_')) return label;
  return label.replaceAll('_', ' ').replace(/\b\w/g, (character) => character.toUpperCase());
}



function fallbackUrgency(task) {
  if (task?.status === 'completed') return { key: 'completed', label: 'Completed' };
  if (!task?.due_at) return { key: 'no_due_date', label: 'No due date' };
  const dueAt = new Date(task.due_at).getTime();
  if (!Number.isFinite(dueAt)) return { key: 'no_due_date', label: 'No due date' };
  const remaining = dueAt - Date.now();
  if (remaining <= 0) return { key: 'overdue', label: 'Overdue' };
  if (remaining <= 24 * 60 * 60 * 1000) return { key: 'due_today', label: 'Due within 24 hours' };
  if (remaining <= 3 * 24 * 60 * 60 * 1000) return { key: 'due_soon', label: 'Due soon' };
  return { key: 'scheduled', label: 'Scheduled' };
}



export function toInboxTask(task) {
  const type = TASK_TYPE_BY_KEY[task?.type_key] || {
    label: humanizeTaskTypeKey(task?.type_label || task?.type_key) || 'Task',
    icon: 'pi pi-check-square',
  };
  const legacyLead = task?.lead || {};
  const lead = task?.lead_context || legacyLead;
  const taskContext = task?.task_context || {};
  const documents = lead.documents || {};
  return {
    id: task?.id,
    paymentReview: task?.payment_review || null,
    subjectType: task?.subject?.type,
    subjectName: task?.payment_review?.expected_identity?.name || lead.name || `Intake Lead #${lead.id}`,
    subjectHref: task?.payment_review?.matter?.href || lead.href || `/intake/leads/${lead.id}`,
    familyKey: task?.family_key,
    familyLabel: task?.family_label,
    typeKey: task?.type_key,
    verb: type.label,
    actionLabel: type.actionLabel || 'Open lead',
    icon: type.icon,
    status: task?.status,
    taskOwner: taskContext.assigned_owner || task?.assignee || null,
    dueAt: taskContext.due_at || task?.due_at || null,
    reason: taskContext.why || task?.reason,
    actionNeeded: taskContext.action_needed || task?.type_label || type.label,
    sourceLabel: taskContext.source || task?.source_label,
    ageLabel: taskContext.age_label || 'Age not recorded',
    urgency: taskContext.urgency || fallbackUrgency(task),
    completedAt: task?.completed_at || null,
    leadId: lead.id,
    leadName: lead.name || `Intake Lead #${lead.id}`,
    prospectEmail: lead.prospect_email || null,
    prospectPhone: lead.prospect_phone || null,
    leadHref: lead.href || `/intake/leads/${lead.id}`,
    defendant: lead.defendant || null,
    company: lead.company || null,
    claimType: lead.claim_type || null,
    state: lead.state || null,
    jurisdiction: lead.jurisdiction || null,
    jurisdictionContext: lead.jurisdiction_context || {},
    leadOwner: lead.assigned_owner || (legacyLead.legal_owner_email ? { email: legacyLead.legal_owner_email } : null),
    stage: lead.intake_stage || legacyLead.current_stage,
    leadStatus: lead.status,
    disposition: lead.disposition || lead.status || null,
    batch: lead.batch || null,
    latestActivity: lead.latest_activity || null,
    documents: {
      receivedCount: Number(documents.received_count ?? legacyLead.document_count) || 0,
      pendingReviewCount: Number(documents.pending_review_count ?? legacyLead.pending_document_review_count) || 0,
      received: documents.received || [],
      outstandingInformation: documents.outstanding_information || [],
    },
    leadSource: lead.source || null,
    convertedMatter: lead.converted_matter || null,
    activities: task?.activities || [],
  };
}
